import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Container, Image, Modal, Nav, NavDropdown, Navbar, Row, Spinner } from 'react-bootstrap';
import { NavLink, useLocation, useMatch, useNavigate } from 'react-router-dom';
import axios from 'axios';

import logo from '../assets/images/avustukset.png';
import { useAuth } from '../contexts/AuthContext.js';
import { useLocalization } from '../contexts/LocalizationContext.js';
import { useConfirm } from '../hooks/useConfirm.js';
import { useApi } from '../hooks/useApi.js';
import { getFullName } from '../utilities/utility.js';

export default function Navigation() {
  const location = useLocation();
  const confirm = useConfirm();
  const auth = useAuth();
  const { localize, locales, setLocale, locale } = useLocalization();
  const [isExpanded, setIsExpanded] = useState(false);

  const applyMatch = useMatch('/apply/:tenantId/*');
  const applyTenantId = applyMatch?.params?.tenantId;
  const tenantMatch = useMatch('/tenants/:tenantId/*');
  const tenantId = tenantMatch?.params?.tenantId;

  useEffect(() => {
    setIsExpanded(false);
  }, [location.pathname]);

  const handleLogout = () => {
    confirm.confirm({
      header: localize('common.logout'),
      message: localize('common.logout.confirm'),
      variant: 'primary',
      okValue: localize('common.yes'),
      cancelValue: localize('common.cancel'),
      onConfirm: auth.logout
    });
  };

  // Laske oikea kotisivu käyttäjäroolin ja URL-kontekstin perusteella
  const applicantTenantId = auth.user?.applicantTenantId;
  const homeUrl = !auth.user ? '/'
    : auth.hasRole('root')
      ? (tenantId ? `/tenants/${tenantId}` : '/admin/tenants')
      : auth.user.tenants?.length > 0
        ? `/tenants/${auth.user.tenants[0].tenantId}`
        : (applyTenantId || applicantTenantId)
          ? `/apply/${applyTenantId || applicantTenantId}`
          : '/apply';

  return (
    <Navbar
      expanded={isExpanded}
      expand='md'
      fixed='top'
      variant='dark'
      bg='dark'
      className='px-4 py-2 border-bottom shadow-sm'>
      <Container fluid='xxl'>
        <Navbar.Brand
          {...(auth?.user ? { as: NavLink, to: homeUrl } : {})}
          className='d-flex align-items-center'>
          <Image
            src={logo}
            alt={(window._env?.appTitle || localize('app.title')) + ' logo'}
            width='auto'
            height='48'
            className='me-2'
          />
          {window._env?.appTitle || localize('app.title')}
        </Navbar.Brand>

        <Navbar.Toggle
          aria-controls='main-navbar-nav'
          onClick={() => setIsExpanded((prev) => !prev)}
        />
        <Navbar.Collapse id='main-navbar-nav'>
          <Nav className='me-auto'>
            <NavLinks auth={auth} localize={localize} />
          </Nav>

          {auth.user && (
            <Nav align='end' className='ms-auto'>
              <UserMenu
                auth={auth}
                localize={localize}
                locales={locales}
                locale={locale}
                setLocale={setLocale}
                onLogout={handleLogout}
              />
            </Nav>
          )}
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

// ─── User menu (top right) ────────────────────────────────────────────────────

function UserMenu({ auth, localize, locales, locale, setLocale, onLogout }) {
  const tenantMatch = useMatch('/tenants/:tenantId/*');
  const tenantId = tenantMatch?.params?.tenantId;
  const navigate = useNavigate();
  // Org name for staff users
  const staffOrg = !auth.hasRole('root') && tenantId
    ? auth.user.tenants?.find((t) => String(t.tenantId) === tenantId)
    : auth.user.tenants?.[0];
  const staffOrgName = staffOrg?.tenantName || null;

  const representedOrg = auth.user?.representedOrganization;

  return (
    <>
    <NavDropdown
      title={getFullName(auth.user)}
      id='nav-user-dropdown'
      align='end'>

      {representedOrg && (
        <>
          <NavDropdown.Header className='text-truncate' style={{ maxWidth: 220 }}>
            {representedOrg.name}
          </NavDropdown.Header>
          <NavDropdown.Item onClick={() => { window.location.href = '/api/auth/suomifi/valtuudet/switch'; }}>
            {localize('nav.switchOrganization') || 'Vaihda organisaatio'}
          </NavDropdown.Item>
          <NavDropdown.Divider />
        </>
      )}

      {staffOrgName && (
        <>
          <NavDropdown.Header className='text-truncate' style={{ maxWidth: 220 }}>
            {staffOrgName}
          </NavDropdown.Header>
          <NavDropdown.Divider />
        </>
      )}

      {auth.hasRole('root') && tenantId && (
        <RootOrgHeader tenantId={tenantId} />
      )}

      {auth.hasRole('root') && (
        <>
          <NavDropdown.Item as={NavLink} to='/admin/tenants'>
            {localize('nav.tenants')}
          </NavDropdown.Item>
          <NavDropdown.Item as={NavLink} to='/admin/help'>
            {localize('nav.helpAdmin')}
          </NavDropdown.Item>
          <NavDropdown.Item as={NavLink} to='/admin/export-api-keys'>
            Export API-avaimet
          </NavDropdown.Item>
          <NavDropdown.Divider />
        </>
      )}

      {locales?.length > 0 && (
        <>
          <NavDropdown.Header>{localize('link.lang')}</NavDropdown.Header>
          {locales.map((lang) => (
            <NavDropdown.Item
              key={`lang-${lang}`}
              active={locale === lang}
              onClick={() => setLocale(lang)}>
              {localize(`lang.${lang}`)}
            </NavDropdown.Item>
          ))}
          <NavDropdown.Divider />
        </>
      )}

      <NavDropdown.Item as={NavLink} to='/help'>
        {localize('nav.help')}
      </NavDropdown.Item>
      <NavDropdown.Divider />
      <NavDropdown.Item onClick={onLogout}>
        {localize('common.logout')}
      </NavDropdown.Item>
    </NavDropdown>

    </>
  );
}

function SwitchOrgModal({ auth, localize, navigate, onHide }) {
  const [organizations, setOrganizations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get('/api/auth/suomifi/my-organizations')
      .then(({ data }) => setOrganizations(data.organizations))
      .catch(() => setError(localize('nav.switchOrg.error') || 'Organisaatioiden haku epäonnistui.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = async (org) => {
    setSwitching(org.yTunnus);
    try {
      await axios.post('/api/auth/suomifi/switch-organization', { yTunnus: org.yTunnus });
      await auth.getUser();
      onHide();
      // Navigoi uuden organisaation portaaliin — tenantId pysyy samana
      const applyMatch = window.location.pathname.match(/^\/apply\/(\d+)/);
      if (applyMatch) navigate(`/apply/${applyMatch[1]}`);
    } catch {
      setError(localize('nav.switchOrg.selectError') || 'Organisaation vaihto epäonnistui.');
      setSwitching(null);
    }
  };

  return (
    <Modal show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{localize('nav.switchOrganization') || 'Vaihda organisaatio'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className='text-center py-3'>
            <Spinner animation='border' size='sm' />
          </div>
        )}
        {error && <div className='text-danger small mb-2'>{error}</div>}
        {!loading && !error && (
          <Row className='g-2'>
            {(organizations || []).map((org) => (
              <Col key={org.yTunnus} xs={12}>
                <Card
                  className={`cursor-pointer${auth.user?.representedOrganization?.yTunnus === org.yTunnus ? ' border-primary' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => !switching && handleSelect(org)}>
                  <Card.Body className='py-2 d-flex justify-content-between align-items-center'>
                    <div>
                      <div className='fw-semibold small'>{org.name}</div>
                      <div className='text-muted' style={{ fontSize: '0.8em' }}>{org.yTunnus}</div>
                    </div>
                    {switching === org.yTunnus
                      ? <Spinner animation='border' size='sm' />
                      : auth.user?.representedOrganization?.yTunnus === org.yTunnus
                        ? <span className='text-primary small'>✓</span>
                        : null}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>{localize('common.cancel') || 'Peruuta'}</Button>
      </Modal.Footer>
    </Modal>
  );
}

function RootOrgHeader({ tenantId }) {
  const { data: tenants } = useApi('admin/tenants');
  const tenant = tenants?.find((t) => String(t.id) === tenantId);
  if (!tenant) return null;
  return (
    <>
      <NavDropdown.Header className='text-truncate' style={{ maxWidth: 220 }}>
        {tenant.name}
      </NavDropdown.Header>
      <NavDropdown.Divider />
    </>
  );
}

// ─── Staff nav links (flat) ───────────────────────────────────────────────────

function StaffNavLinks({ t, localize }) {
  return (
    <>
      <Nav.Link as={NavLink} to={`/tenants/${t.tenantId}`} end>
        {localize('nav.dashboard')}
      </Nav.Link>
      <Nav.Link as={NavLink} to={`/tenants/${t.tenantId}/applications`}>
        {localize('nav.applications')}
      </Nav.Link>
      <Nav.Link as={NavLink} to={`/tenants/${t.tenantId}/applicants`}>
        Hakijat
      </Nav.Link>
      <Nav.Link as={NavLink} to={`/tenants/${t.tenantId}/year-calendar`}>
        Vuosikello
      </Nav.Link>
      <Nav.Link as={NavLink} to={`/tenants/${t.tenantId}/reports`}>
        {localize('nav.reports')}
      </Nav.Link>
      <Nav.Link as={NavLink} to={`/tenants/${t.tenantId}/surveys`}>
        {localize('nav.surveys')}
      </Nav.Link>
      {t.role === 'ylläpitäjä' && (
        <NavDropdown id='nav-admin-dropdown' title={localize('nav.admin') || 'Hallinta'}>
          <NavDropdown.Item as={NavLink} to={`/tenants/${t.tenantId}/grant-types`}>
            {localize('nav.grantTypes')}
          </NavDropdown.Item>
          <NavDropdown.Item as={NavLink} to={`/tenants/${t.tenantId}/form-templates`}>
            {localize('nav.formTemplates')}
          </NavDropdown.Item>
          <NavDropdown.Item as={NavLink} to={`/tenants/${t.tenantId}/grant-rounds`}>
            {localize('nav.grantRounds')}
          </NavDropdown.Item>
          <NavDropdown.Item as={NavLink} to={`/tenants/${t.tenantId}/users`}>
            {localize('nav.users')}
          </NavDropdown.Item>
          <NavDropdown.Item as={NavLink} to={`/tenants/${t.tenantId}/message-templates`}>
            {localize('nav.messageTemplates') || 'Viestipohjat'}
          </NavDropdown.Item>
          <NavDropdown.Item as={NavLink} to={`/tenants/${t.tenantId}/decision-templates`}>
            {localize('nav.decisionTemplates') || 'Päätöspohjat'}
          </NavDropdown.Item>
          <NavDropdown.Divider />
          <NavDropdown.Item as={NavLink} to={`/tenants/${t.tenantId}/audit-log`}>
            Käyttöloki
          </NavDropdown.Item>
          <NavDropdown.Divider />
          <NavDropdown.Item as={NavLink} to={`/tenants/${t.tenantId}/archive-config`}>
            Arkistointiasetukset
          </NavDropdown.Item>
          <NavDropdown.Item as={NavLink} to={`/tenants/${t.tenantId}/branding`}>
            Branding-asetukset
          </NavDropdown.Item>
        </NavDropdown>
      )}
    </>
  );
}

// ─── Root nav links (flat, with tenant context) ───────────────────────────────

function RootNavLinks({ localize }) {
  const tenantMatch = useMatch('/tenants/:tenantId/*');
  const tenantId = tenantMatch?.params?.tenantId;

  if (tenantId) {
    return (
      <>
        <Nav.Link as={NavLink} to={`/tenants/${tenantId}`} end>
          {localize('nav.dashboard')}
        </Nav.Link>
        <Nav.Link as={NavLink} to={`/tenants/${tenantId}/applications`}>
          {localize('nav.applications')}
        </Nav.Link>
        <Nav.Link as={NavLink} to={`/tenants/${tenantId}/applicants`}>
          Hakijat
        </Nav.Link>
        <Nav.Link as={NavLink} to={`/tenants/${tenantId}/year-calendar`}>
          Vuosikello
        </Nav.Link>
        <Nav.Link as={NavLink} to={`/tenants/${tenantId}/reports`}>
          {localize('nav.reports')}
        </Nav.Link>
        <Nav.Link as={NavLink} to={`/tenants/${tenantId}/surveys`}>
          {localize('nav.surveys')}
        </Nav.Link>
        <NavDropdown id='nav-root-admin-dropdown' title={localize('nav.admin') || 'Hallinta'}>
          <NavDropdown.Item as={NavLink} to={`/tenants/${tenantId}/grant-types`}>
            {localize('nav.grantTypes')}
          </NavDropdown.Item>
          <NavDropdown.Item as={NavLink} to={`/tenants/${tenantId}/form-templates`}>
            {localize('nav.formTemplates')}
          </NavDropdown.Item>
          <NavDropdown.Item as={NavLink} to={`/tenants/${tenantId}/grant-rounds`}>
            {localize('nav.grantRounds')}
          </NavDropdown.Item>
          <NavDropdown.Item as={NavLink} to={`/tenants/${tenantId}/users`}>
            {localize('nav.users')}
          </NavDropdown.Item>
          <NavDropdown.Item as={NavLink} to={`/tenants/${tenantId}/message-templates`}>
            {localize('nav.messageTemplates') || 'Viestipohjat'}
          </NavDropdown.Item>
          <NavDropdown.Item as={NavLink} to={`/tenants/${tenantId}/decision-templates`}>
            {localize('nav.decisionTemplates') || 'Päätöspohjat'}
          </NavDropdown.Item>
          <NavDropdown.Divider />
          <NavDropdown.Item as={NavLink} to={`/tenants/${tenantId}/audit-log`}>
            Käyttöloki
          </NavDropdown.Item>
          <NavDropdown.Divider />
          <NavDropdown.Item as={NavLink} to={`/tenants/${tenantId}/archive-config`}>
            Arkistointiasetukset
          </NavDropdown.Item>
          <NavDropdown.Item as={NavLink} to={`/tenants/${tenantId}/branding`}>
            Branding-asetukset
          </NavDropdown.Item>
        </NavDropdown>
      </>
    );
  }

  return null;
}

// ─── Applicant nav links ──────────────────────────────────────────────────────

function ApplicantNavLinks({ tenantId, localize }) {
  return (
    <>
      <Nav.Link as={NavLink} to={`/apply/${tenantId}`} end>
        {localize('nav.portal') || 'Avustushaut'}
      </Nav.Link>
      <Nav.Link as={NavLink} to={`/apply/${tenantId}/applications`}>
        {localize('nav.myApplications') || 'Hakemukseni'}
      </Nav.Link>
      <Nav.Link as={NavLink} to={`/apply/${tenantId}/surveys`}>
        {localize('nav.myReports') || 'Raportit'}
      </Nav.Link>
    </>
  );
}

// ─── Router ───────────────────────────────────────────────────────────────────

function NavLinks({ auth, localize }) {
  const applyMatch = useMatch('/apply/:tenantId/*');
  const applyHomeMatch = useMatch('/apply');
  const applyTenantId = applyMatch?.params?.tenantId;

  if (!auth.user) return null;

  if (applyHomeMatch || applyTenantId) {
    const isStaff = applyTenantId && auth.user.tenants?.some((t) => String(t.tenantId) === applyTenantId);
    if (!isStaff && !auth.hasRole('root')) {
      if (applyTenantId) {
        return <ApplicantNavLinks tenantId={applyTenantId} localize={localize} />;
      }
      return (
        <Nav.Link as={NavLink} to='/apply'>
          {localize('nav.applyHome') || 'Avustushaut'}
        </Nav.Link>
      );
    }
  }

  if (auth.hasRole('root')) {
    return <RootNavLinks localize={localize} />;
  }

  const tenants = auth.user.tenants || [];
  if (tenants.length === 0) return null;

  if (tenants.length === 1) {
    return <StaffNavLinks t={tenants[0]} localize={localize} />;
  }

  // Multiple tenants: group by org
  return (
    <>
      {tenants.map((t) => (
        <NavDropdown key={t.tenantId} id={`nav-tenant-${t.tenantId}-dropdown`} title={t.tenantName}>
          <StaffNavLinks t={t} localize={localize} />
        </NavDropdown>
      ))}
    </>
  );
}
